import { DateInput } from "./DateInput";
import { useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";

import { tr, lbl } from "../lib/i18n";
const STAGES = ["New", "Contacted", "Interested", "Negotiating", "Test Drive Done", "Won", "Lost", "Dormant"];
const TYPES = ["Buyer", "Consignment Owner", "Supplier", "Both"];
const SOURCES = ["Walk-in", "Website", "Live Chat", "WhatsApp", "Referral", "Social", "Phone"];
const KEYS = ["name", "mobile", "email", "customer_type", "source", "stage", "follow_up_date", "address", "notes"];
const field = "w-full h-10 rounded-lg border border-[hsl(var(--border))] px-3 text-sm";

/** Edit a customer / lead (lead = customer in a pipeline stage). */
export const CustomerEditModal = ({ customer, onClose, onSaved }) => {
  const [form, setForm] = useState(() => Object.fromEntries(KEYS.map((k) => [k, customer[k] ?? ""])));
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    if (!form.name.trim() || !form.mobile.trim()) { toast.error(tr("x.name_and_mobile_are_required")); return; }
    const patch = Object.fromEntries(KEYS.filter((k) => (form[k] ?? "") !== (customer[k] ?? "")).map((k) => [k, form[k]]));
    if (!Object.keys(patch).length) { onClose(); return; }
    setBusy(true);
    try {
      const { data } = await api.patch(`/customers/${customer.id}`, patch);
      toast.success(tr("x.customer_saved"));
      onSaved(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const select = (k, opts) => (
    <select className={field} value={form[k]} onChange={set(k)} data-testid={`edit-${k}`}>
      {opts.map((o) => <option key={o} value={o}>{lbl(o)}</option>)}
    </select>
  );
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="panel p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="customer-edit-modal">
        <div className="eyebrow mb-3">{tr("x.edit_customer")}</div>
        <div className="space-y-3">
          <input className={field} placeholder={tr("x.full_name")} value={form.name} onChange={set("name")} data-testid="edit-name" />
          <input className={field} placeholder={tr("x.mobile")} value={form.mobile} onChange={set("mobile")} data-testid="edit-mobile" />
          <input className={field} placeholder={tr("x.email")} value={form.email} onChange={set("email")} data-testid="edit-email" />
          {select("customer_type", TYPES)}
          {select("source", SOURCES)}
          {select("stage", STAGES)}
          <DateInput className={field} type="date" value={form.follow_up_date} onChange={set("follow_up_date")} data-testid="edit-follow_up_date" />
          <input className={field} placeholder={tr("x.address")} value={form.address} onChange={set("address")} data-testid="edit-address" />
          <textarea className={field} style={{ height: 70, padding: 10 }} placeholder={tr("x.notes")} value={form.notes} onChange={set("notes")} data-testid="edit-notes" />
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="btn-secondary" data-testid="edit-cancel">{tr("x.cancel")}</button>
          <button onClick={save} disabled={busy} className="btn-primary" data-testid="edit-save">{busy ? tr("x.saving") : tr("x.save")}</button>
        </div>
      </div>
    </div>
  );
};
